import { Dialog } from "primereact/dialog";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOptions } from "../hooks/use-optionsData";
import Select from "react-select";
import PPDecimalInput from "../../common/PPDecimalInput";

function PPPriceManagementConditionModal({ visible, close, onConditionSave, initialCondition = null }) {
     const [t] = useTranslation();
     const { getConditionOptions, fetchProductAttributeStringValues, fetchProducers, fetchOnlineShops } = useOptions();
     
     const options = getConditionOptions();
     
     const [localState, setLocalState] = useState({
          selectedShopAttribute: null,
          selectedImportedAttribute: null,
          comparisonOperator: null,
          comparisonValue: ""
     });
     
     const [equation, setEquation] = useState([]);
     const [stringValueOptions, setStringValueOptions] = useState([]);
     const [loadingStringValues, setLoadingStringValues] = useState(false);
     const [producerOptions, setProducerOptions] = useState([]);
     const [loadingProducers, setLoadingProducers] = useState(false);
     const [onlineShopOptions, setOnlineShopOptions] = useState([]);
     const [loadingOnlineShops, setLoadingOnlineShops] = useState(false);
     
     useEffect(() => {
          if (visible && initialCondition) {
               if (initialCondition.equation) {
                    setEquation(initialCondition.equation);
               }
          } else if (visible) {
               setLocalState({
                    selectedShopAttribute: null,
                    selectedImportedAttribute: null,
                    comparisonOperator: null,
                    comparisonValue: ""
               });
               setEquation([]);
               setStringValueOptions([]);
               setProducerOptions([]);
               setOnlineShopOptions([]);
          }
     }, [visible, initialCondition]);

     useEffect(() => {
          const fetchStringValues = async () => {
               const importedType = localState.selectedImportedAttribute?.type;
               if (localState.selectedImportedAttribute &&
                   (importedType === "string" || importedType === "tag") &&
                   fetchProductAttributeStringValues) {
                    
                    setLoadingStringValues(true);
                    const stringValues = await fetchProductAttributeStringValues(
                         localState.selectedImportedAttribute.value
                    );
                    setStringValueOptions(stringValues || []);
                    setLoadingStringValues(false);
               } else {
                    setStringValueOptions([]);
                    setLoadingStringValues(false);
               }
          };

          fetchStringValues();
     }, [localState.selectedImportedAttribute, fetchProductAttributeStringValues]);

     useEffect(() => {
          if (localState.selectedShopAttribute) {
               setStringValueOptions([]);
               setLoadingStringValues(false);
          }
     }, [localState.selectedShopAttribute]);

     useEffect(() => {
          const fetchProducerOptions = async () => {
               if (localState.selectedShopAttribute && 
                   localState.selectedShopAttribute.value === "HERSTELLER" && 
                   fetchProducers) {
                    
                    setLoadingProducers(true);
                    const producers = await fetchProducers();
                    setProducerOptions(producers || []);
                    setLoadingProducers(false);
               } else {
                    setProducerOptions([]);
                    setLoadingProducers(false);
               }
          };

          fetchProducerOptions();
     }, [localState.selectedShopAttribute, fetchProducers]);

     useEffect(() => {
          const fetchOnlineShopOptions = async () => {
               if (
                    localState.selectedShopAttribute && 
                    localState.selectedShopAttribute.value === "ONLINESHOP" && 
                    fetchOnlineShops
               ) {
                    
                    setLoadingOnlineShops(true);
                    const shops = await fetchOnlineShops();
                    setOnlineShopOptions(shops || []);
                    setLoadingOnlineShops(false);
               } else {
                    setOnlineShopOptions([]);
                    setLoadingOnlineShops(false);
               }
          };

          fetchOnlineShopOptions();
     }, [localState.selectedShopAttribute, fetchOnlineShops]);
     
     const shopAttributeOptions = options.shopAttributeOptions || [];
     const operatorOptions = options.operators || [];
     const stringOperatorOptions = options.stringOperators || [{ value: "=", label: "=" }];
     const importedAttributeOptions = options.importedAttributeKeys || [];
     
     const addAttributeTerm = () => {
          if (!localState.selectedShopAttribute && !localState.selectedImportedAttribute) {
               return;
          }
          
          const term = {
               type: 'attribute',
               shopAttribute: localState.selectedShopAttribute,
               importedAttribute: localState.selectedImportedAttribute,
               operator: localState.comparisonOperator,
               value: localState.comparisonValue
          };
          
          setEquation(prev => [...prev, term]);
          
          setLocalState({
               selectedShopAttribute: null,
               selectedImportedAttribute: null,
               comparisonOperator: null,
               comparisonValue: ""
          });
     };
     
     const addOperator = (operator) => {
          if (equation.length === 0 || equation[equation.length - 1].type === 'operator') {
               return;
          }
          
          setEquation(prev => [...prev, { type: 'operator', value: operator }]);
     };
     
     const addBracket = (bracket) => {
          setEquation(prev => [...prev, { type: 'bracket', value: bracket }]);
     };
     
     const removeLastTerm = () => {
          setEquation(prev => prev.slice(0, -1));
     };
     
     const clearAll = () => {
          setEquation([]);
     };
     
     const handleSave = () => {
          if (equation.length === 0) {
               return;
          }
          
          const condition = {
               equation: equation,
               description: generateConditionDescription()
          };
          
          onConditionSave(condition);
          close();
     };
     
     const generateConditionDescription = () => {
          if (equation.length === 0) return "Keine Bedingung";
          
          return equation.map(term => {
               if (term.type === 'attribute') {
                    const attr = term.shopAttribute || term.importedAttribute;
                    return `${attr?.label || 'Attribut'} ${term.operator?.label || ''} ${term.value}`;
               } else if (term.type === 'operator') {
                    return term.value;
               } else if (term.type === 'bracket') {
                    return term.value;
               }
               return '';
          }).join(' ');
     };
     
     const renderEquationPreview = () => {
          if (equation.length === 0) {
               return <span className="text-muted">Keine Bedingung definiert</span>;
          }
          
          return equation.map((term, index) => {
               let displayText = '';
               let className = 'badge me-1 ';
               
               if (term.type === 'attribute') {
                    const attr = term.shopAttribute || term.importedAttribute;
                    displayText = `${attr?.label || 'Attribut'} ${term.operator?.label || ''} ${term.value}`;
                    className += 'bg-primary';
               } else if (term.type === 'operator') {
                    displayText = term.value;
                    className += 'bg-secondary';
               } else if (term.type === 'bracket') {
                    displayText = term.value;
                    className += 'bg-secondary';
               }
               
               return (
                    <span key={index} className={className}>
                         {displayText}
                    </span>
               );
          });
     };
     
     const getValuePlaceholder = () => {
          if (localState.selectedImportedAttribute) {
               return `${localState.selectedImportedAttribute.attributeKey || 'Wert'} eingeben`;
          } else if (localState.selectedShopAttribute?.value === "HERSTELLER") {
               return "z.B. Samsung, Apple";
          } else if (localState.selectedShopAttribute?.value === "ONLINESHOP") {
               return "z.B. amazon.de";
          } else if (localState.selectedShopAttribute?.value === "GESAMTPREIS") {
               return "z.B. 99.99";
          }
          return "Wert eingeben";
     };

     const getValueLabel = () => {
          if (localState.selectedImportedAttribute) {
               return `Wert: ${localState.selectedImportedAttribute.attributeKey || ''}`;
          }
          return "Wert";
     };

     const isTextualAttributeType = (attributeType) => attributeType === "string" || attributeType === "tag";

     const getCurrentAttributeType = () => {
          if (localState.selectedShopAttribute) {
               return localState.selectedShopAttribute.attributeType;
          } else if (localState.selectedImportedAttribute) {
               return localState.selectedImportedAttribute.type;
          }
          return null;
     };

     const isNumericAttribute = () => {
          const attributeType = getCurrentAttributeType();
          return attributeType === "numeric";
     };

     const selectedStringOperator = localState.comparisonOperator?.value || "=";
     const usesFreeTextValue = isTextualAttributeType(getCurrentAttributeType()) &&
          (selectedStringOperator === "IN" ||
               selectedStringOperator === "CONTAINS" ||
               (getCurrentAttributeType() === "tag" && selectedStringOperator === "="));

     const getStringValueOptions = () => {
          if (localState.selectedShopAttribute && localState.selectedShopAttribute.attributeType === "string") {
               if (localState.selectedShopAttribute.value === "HERSTELLER") {
                    return producerOptions;
               } else if (localState.selectedShopAttribute.value === "ONLINESHOP") {
                    return onlineShopOptions;
               }
               return [];
          } else if (localState.selectedImportedAttribute &&
               isTextualAttributeType(localState.selectedImportedAttribute.type)) {
               return stringValueOptions;
          }
          return [];
     };

     const getAvailableOperators = () => {
          const attributeType = getCurrentAttributeType();
          if (isTextualAttributeType(attributeType)) {
               return stringOperatorOptions;
          }
          return operatorOptions;
     };

     const isLoadingValues = () => {
          return loadingStringValues || loadingProducers || loadingOnlineShops;
     };
     
     return (
          <Dialog
               header="Bedingung definieren"
               footer={
                    <>
                         <button className="btn btn-danger btn-lg m-2" onClick={close}>
                              {t("single.cancel")}
                         </button>
                         <button 
                              className="btn btn-primary btn-lg m-2" 
                              onClick={handleSave}
                              disabled={equation.length === 0}
                         >
                              <i className="fas fa-save me-2"></i>{' '}Bedingung speichern
                         </button>
                    </>
               }
               visible={visible} 
               style={{ width: '70vw', height: '80vh' }}
               onHide={close}
          >
               <div className="pt-3">
                    <div className="card">
                         <div className="card-header">
                              <h5 className="mb-0">Bedingung erstellen</h5>
                         </div>
                         <div className="card-body">
                              <div className="row mb-3">
                                   <div className="col-md-6">
                                        <label htmlFor="condition-shop-attribute" className="form-label fw-semibold">Shop Attribut</label>
                                        <Select
                                             inputId="condition-shop-attribute"
                                             className="w-100"
                                             options={shopAttributeOptions}
                                             value={localState.selectedShopAttribute}
                                             onChange={(element) => setLocalState(prev => ({ 
                                                  ...prev, 
                                                  selectedShopAttribute: element,
                                                  selectedImportedAttribute: null,
                                                  comparisonOperator: element?.attributeType === "string" ? { value: "=", label: "=" } : null,
                                                  comparisonValue: ""
                                             }))}  
                                             placeholder="Shop Attribut wählen"
                                             isClearable
                                        />
                                   </div>
                                   <div className="col-md-6">
                                        <label htmlFor="condition-imported-attribute" className="form-label fw-semibold">Importierte Attribute</label>
                                        <Select
                                             inputId="condition-imported-attribute"
                                             className="w-100"
                                             options={importedAttributeOptions}
                                             value={localState.selectedImportedAttribute}
                                             onChange={(element) => setLocalState(prev => ({ 
                                                  ...prev, 
                                                  selectedImportedAttribute: element,
                                                  selectedShopAttribute: null,
                                                  comparisonOperator: isTextualAttributeType(element?.type) ? { value: "=", label: "=" } : null,
                                                  comparisonValue: ""
                                             }))}
                                             placeholder="Importierte Attribute wählen"
                                             isClearable
                                        />
                                   </div>
                              </div>
                              
                              <div className="row mb-3">
                                   <div className="col-md-4">
                                        <label htmlFor="condition-operator" className="form-label fw-semibold">Operator</label>
                                        <Select
                                             inputId="condition-operator"
                                             className="w-100"
                                             options={getAvailableOperators()}
                                             value={localState.comparisonOperator}
                                             onChange={(element) => setLocalState(prev => ({
                                                  ...prev,
                                                  comparisonOperator: element,
                                                  comparisonValue: ""
                                             }))}
                                             placeholder="Operator wählen"
                                             isClearable
                                             isDisabled={!getCurrentAttributeType()}
                                        />
                                   </div>
                                   <div className="col-md-8">
                                        <label className="form-label fw-semibold">{getValueLabel()}</label>
                                        {isNumericAttribute() ? (
                                             <PPDecimalInput
                                                  className="form-control"
                                                  value={localState.comparisonValue}
                                                  onValueChange={(v) => setLocalState(prev => ({ ...prev, comparisonValue: v ?? "" }))}
                                                  decimalsLimit={4}
                                                  disableGroupSeparators
                                                  allowNegativeValue
                                                  placeholder="Numerischen Wert eingeben"
                                             />
                                        ) : usesFreeTextValue ? (
                                             <input
                                                  type="text"
                                                  className="form-control"
                                                  value={localState.comparisonValue}
                                                  onChange={(e) => setLocalState(prev => ({ ...prev, comparisonValue: e.target.value }))}
                                                  placeholder={
                                                       selectedStringOperator === "IN"
                                                            ? "tag1, tag2, tag3"
                                                            : getCurrentAttributeType() === "tag"
                                                                 ? "Tag eingeben"
                                                                 : "Suchbegriff"
                                                  }
                                             />
                                        ) : getCurrentAttributeType() === "string" || getCurrentAttributeType() === "tag" ? (
                                             <div className="position-relative">
                                                  <Select
                                                       className="w-100"
                                                       options={getStringValueOptions()}
                                                       value={localState.comparisonValue ? { 
                                                            value: localState.comparisonValue, 
                                                            label: localState.comparisonValue 
                                                       } : null}
                                                       onChange={(element) => setLocalState(prev => ({ 
                                                            ...prev, 
                                                            comparisonValue: element?.value || "" 
                                                       }))}
                                                       placeholder={isLoadingValues() ? "Lade Werte..." : "Wert wählen"}
                                                       isClearable
                                                       isLoading={isLoadingValues()}
                                                       isDisabled={isLoadingValues()}
                                                       loadingMessage={() => "Lade verfügbare Werte..."}
                                                       noOptionsMessage={() => isLoadingValues() ? "Lade..." : "Keine Optionen verfügbar"}
                                                  />
                                                  {isLoadingValues() && (
                                                       <div className="position-absolute top-50 end-0 translate-middle-y me-3" style={{ pointerEvents: 'none', zIndex: 10 }}>
                                                            <output className="spinner-border spinner-border-sm text-primary">
                                                                 <span className="visually-hidden">Loading...</span>
                                                            </output>
                                                       </div>
                                                  )}
                                             </div>
                                        ) : (
                                             <input
                                                  type="text"
                                                  className="form-control"
                                                  value={localState.comparisonValue}
                                                  onChange={(e) => setLocalState(prev => ({ ...prev, comparisonValue: e.target.value }))}
                                                  placeholder={getValuePlaceholder()}
                                             />
                                        )}
                                   </div>
                              </div>
                              
                              <div className="row mb-4">
                                   <div className="col-12">
                                        <button 
                                             className="btn btn-success me-2"
                                             onClick={addAttributeTerm}
                                             disabled={(!localState.selectedShopAttribute && !localState.selectedImportedAttribute) || 
                                                      !localState.comparisonOperator || 
                                                      !localState.comparisonValue}
                                        >
                                             <i className="fas fa-plus me-1"></i>{' '}Attribut hinzufügen
                                        </button>
                                        
                                        <button 
                                             className="btn btn-outline-secondary me-2"
                                             onClick={() => addBracket('(')}
                                        >
                                             (
                                        </button>
                                        
                                        <button 
                                             className="btn btn-outline-secondary me-2"
                                             onClick={() => addBracket(')')}
                                        >
                                             )
                                        </button>
                                        
                                        <button 
                                             className="btn btn-outline-secondary me-2"
                                             onClick={() => addOperator('AND')}
                                        >
                                             AND
                                        </button>
                                        
                                        <button 
                                             className="btn btn-outline-secondary me-2"
                                             onClick={() => addOperator('OR')}
                                        >
                                             OR
                                        </button>
                                        
                                        <button 
                                             className="btn btn-outline-danger me-2"
                                             onClick={removeLastTerm}
                                             disabled={equation.length === 0}
                                        >
                                             <i className="fas fa-backspace me-1"></i>{' '}Löschen
                                        </button>
                                        
                                        <button 
                                             className="btn btn-outline-danger"
                                             onClick={clearAll}
                                             disabled={equation.length === 0}
                                        >
                                             <i className="fas fa-trash me-1"></i>{' '}Alle löschen
                                        </button>
                                   </div>
                              </div>
                              
                              <div className="border-top pt-3">
                                   <h6 className="mb-2">Bedingungsvorschau:</h6>
                                   <div className="bg-light p-3 rounded">
                                        {renderEquationPreview()}
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>
          </Dialog>
     );
}

export default PPPriceManagementConditionModal;
